// client/src/pages/secure/OpenHousesPage.js — schedule open houses, open the tablet
// sign-in page, see who came, close the event, print the seller's report (counts only).
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';
import { Card, Empty, ErrorLine, Btn, Tabs, fmtDate, fmtDateTime, input } from '../../components/secure/territory/Shared';
import DataList from '../../components/shared/DataList';

const NewEvent = ({ onMade }) => {
    const api = useApiClient();
    const me = getCurrentUser();
    const mgr = canManageStaff(me);
    const [props, setProps] = useState([]);
    const [agents, setAgents] = useState([]);
    const [f, setF] = useState({ property_id: '', host_agent_id: '', date: '', from: '13:00', to: '15:00', notes: '' });
    const [error, setError] = useState('');
    useEffect(() => {
        api.get('/api/properties').then((rows) => setProps(rows.filter((p) => ['For Sale', 'For Lease'].includes(p.status)))).catch(() => {});
        if (mgr) api.get('/api/agents').then((rows) => setAgents(Array.isArray(rows) ? rows : [])).catch(() => {});
    }, [api, mgr]);
    const save = async () => {
        try {
            const e = await api.post('/api/open-houses', {
                property_id: f.property_id, host_agent_id: f.host_agent_id || undefined, notes: f.notes,
                starts_at: new Date(`${f.date}T${f.from}`).toISOString(), ends_at: new Date(`${f.date}T${f.to}`).toISOString(),
            });
            setError(''); onMade(e);
        } catch (err) { setError(err.message); }
    };
    return (
        <Card title="Schedule an open house">
            <div className="grid md:grid-cols-3 gap-3 text-sm">
                <label className="md:col-span-3">Listing
                    <select className={input} value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value })}>
                        <option value="">Choose an active listing…</option>
                        {props.map((p) => <option key={p.property_id} value={p.property_id}>{p.street_address}, {p.city} ({p.status})</option>)}
                    </select></label>
                {mgr && <label className="md:col-span-3">Hosting agent
                    <select className={input} value={f.host_agent_id} onChange={(e) => setF({ ...f, host_agent_id: e.target.value })}>
                        <option value="">Me</option>
                        {agents.filter((a) => a.status === 'Active' || !a.status).map((a) => <option key={a.agent_id} value={a.agent_id}>{a.first_name} {a.last_name}</option>)}
                    </select></label>}
                <label>Date<input type="date" className={input} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></label>
                <label>From<input type="time" className={input} value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} /></label>
                <label>To<input type="time" className={input} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></label>
                <label className="md:col-span-3">Notes (staff only)<input className={input} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></label>
            </div>
            <div className="mt-3"><Btn disabled={!f.property_id || !f.date} onClick={save}>Schedule</Btn></div>
            <ErrorLine error={error} />
        </Card>
    );
};

const Report = ({ id }) => {
    const api = useApiClient();
    const [r, setR] = useState(null);
    useEffect(() => { api.get(`/api/open-houses/${id}/report`).then(setR).catch(() => {}); }, [api, id]);
    if (!r) return null;
    return (
        <div className="bg-white border rounded p-4 print:border-0" id="seller-report">
            <h3 className="font-semibold text-lg">Open house report — {r.address}</h3>
            <p className="text-sm text-slate-600">{fmtDateTime(r.starts_at)} to {fmtDateTime(r.ends_at)}{r.host_name ? ` · hosted by ${r.host_name}` : ''}</p>
            <table className="text-sm mt-3"><tbody>
                <tr><td className="pr-6">Visitors who signed in</td><td className="font-semibold">{r.visitors}</td></tr>
                <tr><td className="pr-6">Working with an agent</td><td className="font-semibold">{r.working_with_agent}</td></tr>
                <tr><td className="pr-6">Not working with an agent</td><td className="font-semibold">{r.not_working_with_agent}</td></tr>
            </tbody></table>
            {r.by_hour.length > 0 && <div className="mt-3 text-sm">By hour: {r.by_hour.map((h) => `${h.hour}:00 — ${h.visitors}`).join(' · ')}</div>}
            <p className="text-xs text-slate-500 mt-3">Counts only. Visitors' names and contact details are not shared.</p>
            <div className="mt-2 print:hidden"><Btn kind="secondary" onClick={() => window.print()}>Print</Btn></div>
        </div>
    );
};

const Detail = ({ id, onBack }) => {
    const api = useApiClient();
    const [e, setE] = useState(null);
    const [error, setError] = useState('');
    const [showReport, setShowReport] = useState(false);
    const load = useCallback(async () => { try { setE(await api.get(`/api/open-houses/${id}`)); } catch (err) { setError(err.message); } }, [api, id]);
    useEffect(() => { load(); }, [load]);
    const act = async (what) => { try { await api.post(`/api/open-houses/${id}/${what}`, {}); load(); } catch (err) { setError(err.message); } };
    if (!e) return <ErrorLine error={error} />;
    return (
        <Card title={`${e.street_address}, ${e.city}`} right={<Btn kind="secondary" onClick={onBack}>Back</Btn>}>
            <p className="text-sm text-slate-600">{fmtDateTime(e.starts_at)} – {fmtDateTime(e.ends_at)} · host {e.host_name || '—'} · <strong>{e.event_status}</strong></p>
            {e.event_status === 'scheduled' && (
                <div className="mt-3 p-3 bg-indigo-50 rounded text-sm">
                    <div className="font-medium">Tablet sign-in page</div>
                    <div className="break-all"><a className="text-indigo-700 underline" href={`/open-house/${e.public_token}`} target="_blank" rel="noopener noreferrer">{e.signin_url}</a></div>
                    <div className="text-xs text-slate-600 mt-1">Open it on the tablet at the door. It accepts sign-ins from two hours before the start until two hours after the end, and stops when you close the event.</div>
                    <div className="flex gap-2 mt-2">
                        <Btn kind="secondary" onClick={() => navigator.clipboard?.writeText(e.signin_url)}>Copy link</Btn>
                        <Btn onClick={() => act('close')}>Close the open house</Btn>
                        <Btn kind="danger" onClick={() => act('cancel')}>Cancel it</Btn>
                    </div>
                </div>
            )}
            <ErrorLine error={error} />
            <h3 className="font-semibold mt-4 mb-2">Sign-ins ({e.signins.length})</h3>
            {e.signins.length === 0 ? <Empty>Nobody yet.</Empty> : (
                <DataList rows={e.signins} rowKey="signin_id" csvName="open-house-sign-ins" defaultSort={{ key: 'signed_in_at', dir: 'asc' }}
                    filters={[{ key: 'has_agent', label: 'Agent?' }]}
                    columns={[
                        { key: 'signed_in_at', label: 'Time', type: 'date', render: (s) => <span className="whitespace-nowrap">{fmtDateTime(s.signed_in_at)}</span> },
                        { key: 'visitor', label: 'Visitor', value: (s) => [`${s.first_name || ''} ${s.last_name || ''}`.trim(), s.email, s.phone].filter(Boolean).join(' · '), sortValue: (s) => `${s.last_name || ''} ${s.first_name || ''}`,
                            render: (s) => <>{s.first_name} {s.last_name}<div className="text-xs text-slate-500">{[s.email, s.phone].filter(Boolean).join(' · ')}</div></> },
                        { key: 'has_agent', label: 'Agent?', value: (s) => (Number(s.has_agent) ? 'Yes' : 'No'),
                            render: (s) => (Number(s.has_agent) ? <span className="text-amber-700">Yes{s.buyer_agent_name ? ` (${s.buyer_agent_name})` : ''} — do not solicit</span> : 'No') },
                        { key: 'buyer_agent_name', label: 'Their agent', render: (s) => s.buyer_agent_name || '' },
                        { key: 'consents', label: 'Consents', value: (s) => (s.consents_granted ? s.consents_granted.replace('calls_texts', 'calls & texts').replace('email_updates', 'email') : 'none'),
                            render: (s) => <span className="text-xs">{s.consents_granted ? s.consents_granted.replace('calls_texts', 'calls & texts').replace('email_updates', 'email') : 'none'}</span> },
                        { key: 'outcome_note', label: 'CRM / outcome', render: (s) => <>{s.crm_contact_id && <Link className="text-indigo-700 underline" to={`/dashboard/crm/contacts/${s.crm_contact_id}`}>CRM card</Link>}
                            {s.outcome_note && <div className="text-xs text-slate-500">{s.outcome_note}</div>}</> },
                    ]} />
            )}
            <div className="mt-4"><Btn kind="secondary" onClick={() => setShowReport(!showReport)}>{showReport ? 'Hide' : 'Seller report (counts only)'}</Btn></div>
            {showReport && <div className="mt-3"><Report id={id} /></div>}
        </Card>
    );
};

const OpenHousesPage = () => {
    const api = useApiClient();
    const [tab, setTab] = useState('upcoming');
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(async () => {
        try { setRows(await api.get(`/api/open-houses?when=${tab === 'past' ? 'past' : 'upcoming'}`)); setError(''); } catch (e) { setError(e.message); }
    }, [api, tab]);
    useEffect(() => { if (tab !== 'new') load(); }, [load, tab]);
    return (
        <div className="p-4 md:p-6 space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Open Houses</h1>
                <p className="text-sm text-slate-600">A tablet sign-in page per open house. Visitors become CRM contacts for the hosting agent; anyone working with an agent is not solicited; each consent is stored with its exact words and time.</p>
            </div>
            {open ? <Detail id={open} onBack={() => { setOpen(null); load(); }} /> : <>
                <Tabs value={tab} onChange={setTab} tabs={[['upcoming', 'Upcoming'], ['past', 'Past'], ['new', 'Schedule']]} />
                <ErrorLine error={error} />
                {tab === 'new' ? <NewEvent onMade={(e) => { setTab('upcoming'); setOpen(e.event_id); }} />
                    : rows.length === 0 ? <Empty>No open houses.</Empty> : (
                        <ul className="divide-y bg-white rounded-lg shadow">{rows.map((e) => (
                            <li key={e.event_id} className="p-3 flex justify-between items-center gap-2">
                                <div className="text-sm"><div className="font-medium">{e.street_address}, {e.city}</div>
                                    <div className="text-slate-500">{fmtDate(e.starts_at)} {fmtDateTime(e.starts_at).split(', ').pop()} · {e.host_name || '—'} · {e.signin_count} sign-in(s) · {e.event_status}</div></div>
                                <Btn kind="secondary" onClick={() => setOpen(e.event_id)}>Open</Btn>
                            </li>))}</ul>
                    )}
            </>}
        </div>
    );
};

export default OpenHousesPage;
